import type { CSSProperties } from 'react';

import type { ToastNotification, ToastPosition } from '../../types/toast-types';

/** Every screen position a toast can target, in mount order. */
export const TOAST_POSITIONS: ToastPosition[] = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
  'center',
];

const DEFAULT_TOAST_POSITION: ToastPosition = 'top-right';
const TOP_TOAST_OFFSET_PX = 40;

const viewportBase =
  'pointer-events-none fixed z-[100] flex max-w-[90vw] flex-col gap-2 sm:max-w-[28.75rem]';

export const viewportClass: Record<ToastPosition, string> = {
  'top-left': `${viewportBase} top-4 left-4 items-start`,
  'top-center': `${viewportBase} top-4 inset-x-0 items-center mx-auto`,
  'top-right': `${viewportBase} top-4 right-4 items-end`,
  'bottom-left': `${viewportBase} inset-x-auto inset-y-auto bottom-4 left-4 items-start`,
  'bottom-center': `${viewportBase} inset-x-0 bottom-4 items-center mx-auto`,
  'bottom-right': `${viewportBase} inset-x-auto inset-y-auto bottom-4 right-4 items-end`,
  center: `${viewportBase} left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center`,
};

/** Top positions get a fixed pixel offset that overrides the `top-4` class. */
export function viewportStyle(
  position: ToastPosition,
): CSSProperties | undefined {
  if (!position.startsWith('top')) return undefined;
  return { top: `${TOP_TOAST_OFFSET_PX}px` };
}

/** The toasts that belong to `position`. A toast with no position goes top-right. */
export function atPosition(
  notifications: ToastNotification[],
  position: ToastPosition,
): ToastNotification[] {
  return notifications.filter(
    (n) => (n.position ?? DEFAULT_TOAST_POSITION) === position,
  );
}
